// Formatting + UI-only alert thresholds for the admin Spotlights dashboard.
// Thresholds are persisted per-admin in localStorage (no backend) and drive the in-dashboard
// "needs attention" highlighting on the Overview / Brands / Creators tabs.

export const fmtMoney = n => `$${Math.round(Number(n) || 0).toLocaleString()}`;
export const fmtMoney2 = n => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtInt = n => (Number(n) || 0).toLocaleString();
export const fmtRoas = n => `${(Number(n) || 0).toFixed(2)}x`;
export const fmtPct = n => `${Math.round((Number(n) || 0) * 100)}%`;

export const SPOTLIGHT_STATUSES = ['uploading', 'readyToLaunch', 'launching', 'experiment', 'validated', 'calibrating', 'paused', 'blocked', 'complete']; // prettier-ignore
export const LIVE_STATUSES = ['experiment', 'validated', 'calibrating'];

// Experiment + validated collapse into one "Live" bucket for display (the API still emits raw
// 'validated' until the migration retires it, so fold on the way in). Calibrating stays its own
// segment — it's the warm-up window, and ops wants to see how many just launched.
export const toDisplayStatus = status => (status === 'experiment' || status === 'validated' ? 'live' : status);

export const DISPLAY_STATUSES = ['uploading', 'readyToLaunch', 'launching', 'live', 'calibrating', 'paused', 'blocked', 'complete']; // prettier-ignore

export const toDisplayLifecycle = (lifecycle = {}) =>
  Object.entries(lifecycle).reduce((acc, [status, count]) => {
    const key = toDisplayStatus(status);
    acc[key] = (acc[key] || 0) + (Number(count) || 0);
    return acc;
  }, {});

export const STATUS_LABELS = {
  uploading: 'Uploading',
  readyToLaunch: 'Ready to launch',
  launching: 'Launching',
  live: 'Live',
  calibrating: 'Calibrating',
  paused: 'Paused',
  blocked: 'Blocked',
  complete: 'Complete'
};

// ---------------------------------------------------------------------------
// Chart palette — derived from the SpotlightChart green (#61be91) by rotating ONLY the hue
// (saturation + lightness fixed) so the whole set is tonally consistent. Shared by the
// Overview donut/trend and the brand-row lifecycle bar.
// ---------------------------------------------------------------------------
export const CHART_S = 60;
export const CHART_L = 70;
export const hue = h => `hsl(${((h % 360) + 360) % 360}, ${CHART_S}%, ${CHART_L}%)`;

// Lifecycle hues grouped by meaning: live → green, ready/complete → lime→blue,
// setup/processing → orange, paused/blocked → red.
export const LIFECYCLE_HUES = {
  paused: 10, // halted → red
  launching: 24, // processing → orange
  uploading: 38, // processing → orange
  readyToLaunch: 80, // about to go live → lime
  calibrating: 50, // warming up → amber
  live: 130, // live → green
  complete: 215, // done → blue
  blocked: 352 // halted → red
};
export const LIFECYCLE_COLORS = Object.keys(LIFECYCLE_HUES).reduce((acc, k) => ({ ...acc, [k]: hue(LIFECYCLE_HUES[k]) }), {});

// ---------------------------------------------------------------------------
// UI-only alert thresholds (localStorage)
// ---------------------------------------------------------------------------
const THRESHOLDS_KEY = 'spotlights_dashboard_thresholds_v1';

export const DEFAULT_THRESHOLDS = {
  lowRoas: 1, // AN ROAS below this (with spend) is flagged
  strongRoas: 2, // AN ROAS at/above this is "strong"
  acceptedNotLaunchedRate: 0.3, // accepted-but-not-launched share that warrants attention
  rejectionRate: 0.4, // brand/creator rejection rate that warrants attention
  pendingAgingDays: 7, // pending auth requests older than this
  noSpendGraceHours: 24 // live + no spend beyond this many hours
};

export const getThresholds = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(THRESHOLDS_KEY) || '{}');
    return { ...DEFAULT_THRESHOLDS, ...stored };
  } catch {
    return { ...DEFAULT_THRESHOLDS };
  }
};

export const saveThresholds = thresholds => {
  window.localStorage.setItem(THRESHOLDS_KEY, JSON.stringify(thresholds));
};

export const THRESHOLD_FIELDS = [
  { key: 'lowRoas', label: 'Low AN ROAS below', type: 'roas', help: 'Live spotlights with spend whose AN ROAS is under this are flagged.' },
  { key: 'strongRoas', label: 'Strong AN ROAS at/above', type: 'roas', help: 'AN ROAS at or above this is highlighted as strong.' },
  {
    key: 'acceptedNotLaunchedRate',
    label: 'Accepted-not-launched rate above',
    type: 'pct',
    help: 'Flag brands whose accepted authorizations that never launched exceed this share.'
  },
  { key: 'rejectionRate', label: 'Rejection rate above', type: 'pct', help: 'Flag brands/creators whose request rejection rate exceeds this.' },
  {
    key: 'pendingAgingDays',
    label: 'Pending request aging (days)',
    type: 'int',
    help: 'Pending authorization requests older than this many days warrant attention.'
  },
  {
    key: 'noSpendGraceHours',
    label: 'No-spend grace (hours)',
    type: 'int',
    help: 'Live spotlights with no spend beyond this many hours are flagged.'
  }
];
